import { useEffect, useState } from 'react';

/**
 * Bailey's voice on the phone: the list the server offers, grouped the way
 * an owner in Britain would look for one (British first), and one sample
 * playing at a time anywhere in the app.
 */

// British first for UK salons; any accent not listed follows in the order the server sent it.
const ACCENT_ORDER = ['British', 'Irish', 'Scottish', 'Welsh', 'Australian', 'American'];

export function tidyVoices(raw) {
    const d = raw || {};
    const voices = (Array.isArray(d.voices) ? d.voices : [])
        .filter((v) => v && v.id && v.name)
        .map((v) => ({
            id: String(v.id),
            provider: String(v.provider || ''),
            name: String(v.name).trim(),
            accent: String(v.accent || '').trim(),
            vibe: String(v.vibe || '').trim(),
            sample: v.previewUrl || v.sampleUrl || null
        }));
    return { voices, defaultId: d.defaultVoiceId ? String(d.defaultVoiceId) : null };
}

export function groupVoices(voices) {
    const groups = [];
    (voices || []).forEach((v) => {
        const accent = v.accent || 'Other';
        let g = groups.find((x) => x.accent === accent);
        if (!g) { g = { accent, voices: [] }; groups.push(g); }
        g.voices.push(v);
    });
    const rank = (a) => { const i = ACCENT_ORDER.indexOf(a); return i === -1 ? ACCENT_ORDER.length : i; };
    return groups
        .map((g, i) => ({ ...g, i }))
        .sort((a, b) => (rank(a.accent) - rank(b.accent)) || (a.i - b.i))
        .map(({ i, ...g }) => g);
}

/** The voice callers hear now: the one chosen, or the server's default when none is. */
export function voiceInUse(settings, list) {
    const s = settings || {};
    const voices = (list && list.voices) || [];
    const chosen = s.voiceId ? voices.find((v) => v.id === String(s.voiceId)) : null;
    if (chosen) return { voice: chosen, isDefault: false };
    const dflt = list && list.defaultId ? voices.find((v) => v.id === list.defaultId) : null;
    // A voice id the list doesn't know yet (retired, or a longer list on the server): still theirs.
    if (s.voiceId) return { voice: null, isDefault: false };
    return { voice: dflt || null, isDefault: !!dflt };
}

/** "Charlie, Australian", or just the name. */
export const voiceTitle = (v) => (v ? (v.accent ? `${v.name}, ${v.accent}` : v.name) : '');

/* ------------------------------------------------------------ samples */
let current = null;           // { id, audio }
const listeners = new Set();
const tell = () => listeners.forEach((f) => f(current ? current.id : null));

export function stopSample() {
    if (!current) return;
    try { current.audio.pause(); } catch (e) { /* already stopped */ }
    current = null;
    tell();
}

export function playSample(voice) {
    stopSample();
    if (!voice || !voice.sample) return;
    let audio;
    try { audio = new Audio(voice.sample); } catch (e) { return; }
    current = { id: voice.id, audio };
    const end = () => { if (current && current.audio === audio) { current = null; tell(); } };
    audio.onended = end;
    audio.onerror = end;
    tell();
    const p = audio.play();
    if (p && p.catch) p.catch(end);
}

/** Which voice is playing, and a play/stop toggle. Stops when the last user goes. */
export function useSample() {
    const [playing, setPlaying] = useState(current ? current.id : null);
    useEffect(() => {
        listeners.add(setPlaying);
        return () => { listeners.delete(setPlaying); if (!listeners.size) stopSample(); };
    }, []);
    return { playing, toggle: (v) => (playing && v && playing === v.id ? stopSample() : playSample(v)) };
}
